// CheckboxQueueSidebar.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/* ---------- Types ---------- */
export type QueueItem = {
  id: string;
  sessionId?: string; // ✅ ADD THIS
  emrId?: string;
  date: string;
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
};

type Props = {
  items?: QueueItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  centerId?: string;
  dateISO?: string;
  autoRefreshMs?: number;
  onItemsLoaded?: (items: QueueItem[]) => void;
  refreshKey?: number;
};

/* ---------- Helper Functions ---------- */
function toTodayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDmy(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function computeAgeYearsFromDob(dob?: string | null) {
  if (!dob) return 0;
  try {
    const bd = new Date(dob);
    const now = new Date();
    let years = now.getFullYear() - bd.getFullYear();
    const m = now.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < bd.getDate())) years--;
    return Math.max(0, years);
  } catch {
    return 0;
  }
}

function deriveCenterIdFromStorage(): string {
  const pick = (o: any) =>
    o?.centerAssignments?.[0]?.centerId ||
    o?.roleAssignments?.[0]?.centerId ||
    "";
  const readJSON = (store: Storage, key: string) => {
    try {
      const raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const sCurr = readJSON(sessionStorage, "currentUser");
  if (sCurr) {
    const cid = pick(sCurr);
    if (cid) return cid;
  }
  const lCurr = readJSON(localStorage, "currentUser");
  if (lCurr) {
    const cid = pick(lCurr);
    if (cid) return cid;
  }
  const plain =
    sessionStorage.getItem("centerId") ||
    localStorage.getItem("centerId") ||
    "";
  return plain || "";
}

/* ---------- Component ---------- */
const CheckboxQueueSidebar: React.FC<Props> = ({
  // items: itemsProp,
  selectedIds,
  onToggle,
  centerId,
  dateISO,
  autoRefreshMs,
  onItemsLoaded,
  refreshKey,
}) => {
  const [query, setQuery] = useState("");
  const [dynItems, setDynItems] = useState<QueueItem[]>([]);
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [effectiveCenterId, setEffectiveCenterId] = useState<string>(
    centerId || deriveCenterIdFromStorage(),
  );
  const effectiveDate = dateISO || toTodayISO();

  /* ✅ Fetch child details (age, gender) from Parent-Child microservice */

  async function fetchChildDetails(
    emrId: string,
    token: string,
  ): Promise<{ genderShort: "M" | "F"; ageYears: number }> {
    try {
      const { data } = await api.parentchildApi.get(
        `/children/emr/${encodeURIComponent(emrId)}/details`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const genderRaw = data?.data?.childGender || "";
      const dobRaw = data?.data?.childDob || null;

      const ageYears = computeAgeYearsFromDob(dobRaw);
      const g = String(genderRaw).toUpperCase();

      return {
        genderShort: g.startsWith("F") ? "F" : "M",
        ageYears,
      };
    } catch {
      return { genderShort: "M", ageYears: 0 };
    }
  }

  /* ✅ Load data from Appointment API, enrich with Parent-Child API */
  async function load() {
    if (!effectiveCenterId) return;
    setLoading(true);
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");

      const { data } = await api.centerworkflowApi.get(
        `/queues/B_AND_U_SAMPLE_COLLECTED_Q?centerId=${encodeURIComponent(
          effectiveCenterId,
        )}&date=${encodeURIComponent(effectiveDate)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const rows = Array.isArray(data?.waiting) ? data.waiting : [];

      const enriched: QueueItem[] = [];
      for (const r of rows) {
        const ts = r.createdAt || r.updatedAt || "";

        const emrId = r.child?.childId;
        const name = r.child?.name;

        let genderShort: "M" | "F" = "M";
        let ageYears = 0;

        // ✅ Enrich from Parent-Child microservice
        if (emrId) {
          const childInfo = await fetchChildDetails(emrId, token);
          genderShort = childInfo.genderShort;
          ageYears = childInfo.ageYears;
        }

        enriched.push({
          id: r._id,
          emrId,
          sessionId: r.sessionId, // ✅ ADD THIS
          date: formatDmy(ts),
          name,
          genderShort,
          ageYears,
          avatarUrl: r.child?.profile,
        });
      }

      setDynItems(enriched);
      onItemsLoaded?.(enriched);
    } catch (e: any) {
      setErr(e?.message || "Failed to load appointments");
      setDynItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [effectiveCenterId, effectiveDate, refreshKey]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(load, autoRefreshMs);
    return () => clearInterval(t);
  }, [effectiveCenterId, effectiveDate, autoRefreshMs]);

  /* ---------- PRESIGNED IMAGE FETCH (FIXED) ---------- */
  useEffect(() => {
    const fetchImages = async () => {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      const map: Record<string, string> = {};

      for (const p of dynItems) {
        // 1. Get path string
        const filePath = p.avatarUrl;

        // Skip if empty
        if (!filePath) continue;

        try {
          // 2. Remove 'new URL()'. Split manually.
          const cleanPath = filePath.startsWith("/")
            ? filePath.slice(1)
            : filePath;

          const parts = cleanPath.split("/");

          if (parts.length < 3) {
            console.warn("Invalid profile key:", filePath);
            continue;
          }

          const module = `${parts[0]}/${parts[1]}`;
          const key = parts.slice(2).join("/");

          // 5. Call API
          const res = await api.S3config.get("/view-url", {
            params: { module, key },
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.data?.url) {
            map[p.id] = res.data.url;
          }
        } catch (e) {
          console.error(`Image fetch failed for ${p.name}`, e);
        }
      }

      setAvatarMap(map);
    };

    if (dynItems.length) fetchImages();
  }, [dynItems]);

  /* ---------- Render ---------- */

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return !q
      ? dynItems
      : dynItems.filter((p) => p.name.toLowerCase().includes(q));
  }, [dynItems, query]);

  const isSel = (id: string) => selectedIds.includes(id);

  return (
    <aside className="cqs">
      <div className="cqs__header">
        <div className="cqs__title">
          Patient Queue
          <span className="cqs__underline" />
        </div>

        <div className="cqs__searchwrap">
          <input
            className="cqs__search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {loading && <div className="cqs__empty">Loading…</div>}
      {err && !loading && <div className="cqs__empty">Error: {err}</div>}
      {!loading && !err && filtered.length === 0 && (
        <div className="cqs__empty">No patients</div>
      )}

      <ul className="cqs__list">
        {filtered.map((p) => {
          const selected = isSel(p.id);
          return (
            <li key={p.id}>
              <label
                className={`cqs__item ${selected ? "is-selected" : ""}`}
                onClick={() => {
                  sessionStorage.setItem(
                    "selectedPatient",
                    JSON.stringify({ ...p, avatarUrl: avatarMap[p.id] }),
                  );
                  window.dispatchEvent(new Event("patientUpdated"));
                }}
              >
                <div className="cqs__avatar">
                  {avatarMap[p.id] ? (
                    <img src={avatarMap[p.id]} alt="" />
                  ) : (
                    <div className="cqs__avatar--fallback" />
                  )}
                </div>
                <div>
                  <div className="cqs__date">{p.date}</div>
                  {/* ✅ Name from Appointment API */}
                  <div className="cqs__name">{p.name}</div>
                  <div className="cqs__sub">
                    {p.genderShort}, {p.ageYears} Years
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="cqs__check"
                  checked={selected}
                  onChange={() => onToggle(p.id)}
                />
              </label>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default CheckboxQueueSidebar;
